import React, { useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';

/** 把工具包下载地址归一到受鉴权的 /api/tools/file，避免 /uploads 被 SPA 回成 index.html。 */
function resolveToolDownloadUrl(rawUrl) {
  const raw = String(rawUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      const m = u.pathname.match(/\/uploads\/tools\/([^/]+)$/);
      if (m) return `/api/tools/file/${encodeURIComponent(decodeURIComponent(m[1]))}`;
    } catch (_) { /* keep absolute */ }
    return raw;
  }
  if (raw.startsWith('/api/tools/file/')) {
    const name = raw.slice('/api/tools/file/'.length).split(/[?#]/)[0];
    if (!name) return raw;
    try {
      return `/api/tools/file/${encodeURIComponent(decodeURIComponent(name))}`;
    } catch (_) {
      return `/api/tools/file/${encodeURIComponent(name)}`;
    }
  }
  if (raw.startsWith('/api/')) return raw;
  const uploadMatch = raw.match(/\/uploads\/tools\/([^/?#]+)/);
  if (uploadMatch) {
    try {
      return `/api/tools/file/${encodeURIComponent(decodeURIComponent(uploadMatch[1]))}`;
    } catch (_) {
      return `/api/tools/file/${encodeURIComponent(uploadMatch[1])}`;
    }
  }
  const filename = raw
    .replace(/^\\?frontend\\public\\locus_profiles\\?/i, '')
    .replace(/^.*[\\/]/, '')
    .replace(/^\/+/, '');
  return filename ? `/api/tools/file/${encodeURIComponent(filename)}` : '';
}

/**
 * 拼出浏览器可直接拉文件的绝对 URL。
 * 本地 CRA(:3000) 必须直连后端 :5000，禁止 location.assign 相对 /api（整页进 SPA 后会被 path=* 打回首页）。
 */
function resolveAbsoluteToolFileUrl(realUrl) {
  const raw = String(realUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (typeof window === 'undefined' || !window.location) return raw;
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  const { hostname, port } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  const isCraDev = !port || port === '3000' || port === '3001' || port === '5001';
  if (isLocal && isCraDev && path.startsWith('/api/')) {
    return `http://127.0.0.1:5000${path}`;
  }
  return `${window.location.origin}${path}`;
}

/** 用临时 <a> 触发下载，不离开当前 SPA 路由（勿用 location.assign / location.href）。 */
function triggerAnchorDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.setAttribute('download', filename);
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function isExternalHttp(url) {
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    const u = new URL(url, window.location.origin);
    return u.origin !== window.location.origin;
  } catch (_) {
    return true;
  }
}

function filenameFromTool(tool, url) {
  const raw = String(url || '');
  if (tool.slug === 'google_earth' || /kml-export\/google-earth/i.test(raw)) {
    return 'ZHXT_GoogleEarth.kml';
  }
  const m = raw.match(/\/([^/?#]+)(?:\?|$)/);
  let name = (m && m[1]) || String(tool.name || 'download').replace(/[\\/]/g, '_');
  try {
    name = decodeURIComponent(name);
  } catch (_) { /* keep encoded */ }
  // APK：优先用 URL 文件名，保证扩展名正确（手机安装依赖 .apk）
  if (/\.apk$/i.test(name)) return name;
  if (tool.slug && /locus/i.test(String(tool.slug)) && !/\.\w+$/.test(name)) {
    return 'LocusMap.apk';
  }
  return name;
}

async function downloadWithAuth(tool, url) {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      window.alert('请先登录后再下载此工具（需要登录权限，未登录无法下载）');
      return;
    }
    const res = await apiFetch(url);
    if (!res.ok) {
      let msg = '下载失败';
      try {
        const data = await res.json();
        msg = [data.error || msg, data.detail].filter(Boolean).join('；');
      } catch (_) { /* ignore */ }
      if (res.status === 403) {
        msg = msg && /无权|权限|forbidden/i.test(msg)
          ? msg
          : `无权下载此工具（403）。请确认已登录且账号有权限；${msg}`;
      } else if (res.status === 401) {
        msg = `未登录或登录已过期（401）。请重新登录后再试；${msg}`;
      } else if (res.status === 404) {
        msg = `文件不存在（404）。请联系管理员上传；${msg}`;
      }
      window.alert(msg);
      return;
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filenameFromTool(tool, url);
    document.body.appendChild(a);
    a.click();
    a.remove();
    // 立刻 revoke 会导致部分浏览器（尤其 Chromium）取消 blob 下载
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (err) {
    window.alert(`下载失败：${err && err.message ? err.message : String(err)}`);
  }
}

function ToolsPage() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/tools/list')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setTools(data.tools || []);
        } else {
          setError(data.error || '获取工具失败');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('网络错误，无法获取工具列表');
        setLoading(false);
      });
  }, []);

  const handleDownload = (tool) => {
    const realUrl = resolveToolDownloadUrl(tool.url);
    if (!realUrl) return;
    // 非 public（audience 限制）或绑定了分组：一律走鉴权下载；勿改库里 audience 为 public 来绕过
    const restricted =
      (tool.audience && tool.audience !== 'public') ||
      (Array.isArray(tool.group_ids) && tool.group_ids.length > 0);
    if (isExternalHttp(realUrl) && !restricted) {
      window.open(realUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // 非公开：必须 Bearer（downloadWithAuth）；无 token 直链会 403「无权下载」
    if (restricted) {
      downloadWithAuth(tool, realUrl);
      return;
    }
    // 公开工具包：临时 <a> + 绝对 URL；禁止 location.assign（会离开 /tools，错 URL/回 HTML 被 path=* 打回首页）
    if (/^\/api\/tools\/file\//.test(realUrl)) {
      triggerAnchorDownload(
        resolveAbsoluteToolFileUrl(realUrl),
        filenameFromTool(tool, realUrl)
      );
      return;
    }
    if (realUrl.startsWith('/api/')) {
      downloadWithAuth(tool, realUrl);
      return;
    }
    triggerAnchorDownload(
      resolveAbsoluteToolFileUrl(realUrl),
      filenameFromTool(tool, realUrl)
    );
  };

  const containerStyle = {
    maxWidth: 600,
    margin: '20px auto',
    padding: 20,
    border: '1px solid #e0e0e0',
    borderRadius: 10,
    background: '#fafbfc',
    boxShadow: '0 2px 12px #0001',
  };
  const titleStyle = {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 24,
    color: '#1976d2',
    textAlign: 'center',
    letterSpacing: 2
  };
  const listStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  };
  const itemStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#fff',
    borderRadius: 6,
    padding: '14px 18px',
    boxShadow: '0 1px 4px #0001',
    fontSize: 17
  };
  const btnStyle = {
    background: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '7px 18px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  };

  const mediaQuery = `@media (max-width: 600px) { .tools-list-item { flex-direction: column; align-items: flex-start; gap: 8px; } }`;

  return (
    <div style={containerStyle}>
      <style>{mediaQuery}</style>
      <div style={titleStyle}>工具下载</div>
      {loading && <div style={{ textAlign: 'center', color: '#888' }}>加载中...</div>}
      {error && <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>}
      <div style={listStyle}>
        {tools.map(tool => {
          const realUrl = resolveToolDownloadUrl(tool.url);
          return (
            <div key={tool.id}>
              <div className="tools-list-item" style={itemStyle}>
                <span>{tool.name}</span>
                {realUrl ? (
                  <button type="button" style={btnStyle} onClick={() => handleDownload(tool)}>
                    下载
                  </button>
                ) : (
                  <button type="button" style={{ ...btnStyle, background: '#9e9e9e', cursor: 'not-allowed' }} disabled>
                    无链接
                  </button>
                )}
              </div>
              {tool.description ? (
                <div style={{ color: '#888', fontSize: 13, lineHeight: 1.5, padding: '6px 4px 0' }}>
                  {tool.description}
                </div>
              ) : null}
            </div>
          );
        })}
        {!loading && tools.length === 0 && !error && (
          <div style={{ color: '#888', textAlign: 'center', marginTop: 8, fontSize: 13 }}>
            暂无你可下载的工具。请登录后重试，或联系管理员开通权限。
          </div>
        )}
      </div>
    </div>
  );
}

export default ToolsPage;
